import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { enablePushNotifications } from "@/lib/push";
import { toast } from "sonner";
import {
  Baby, LayoutDashboard, BookOpen, CalendarCheck, Users, Coins,
  FileText, Wallet, LogOut, Menu, Link2, X, Settings, MessageCircle, Files, Bell,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/bimbi", label: "Registro Bimbi", icon: Baby, testid: "nav-bimbi" },
  { to: "/diario", label: "Diario & Foto", icon: BookOpen, testid: "nav-diario" },
  { to: "/presenze", label: "Presenze", icon: CalendarCheck, testid: "nav-presenze" },
  { to: "/comunicazioni", label: "Comunicazioni", icon: MessageCircle, testid: "nav-comunicazioni" },
  { to: "/documenti-genitori", label: "Moduli Compilati", icon: Files, testid: "nav-documenti-genitori" },
  { to: "/deleghe", label: "Deleghe", icon: FileText, testid: "nav-deleghe" },
  { to: "/buste-paga-maestra", label: "Buste paga", icon: Wallet, testid: "nav-buste-paga-maestra" },
  { to: "/staff", label: "Staff & Buste Paga", icon: Users, testid: "nav-staff" },
  { to: "/incassi", label: "Incassi", icon: Coins, testid: "nav-incassi" },
  { to: "/fatture", label: "Fatture", icon: FileText, testid: "nav-fatture" },
  { to: "/prima-nota", label: "Prima Nota", icon: Wallet, testid: "nav-prima-nota" },
  { to: "/modulistica", label: "Modulistica", icon: Files, testid: "nav-modulistica" },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, testid: "nav-impostazioni" },
];

const BRAND_CACHE_KEY = "apiccolipassi_branding";

function cachedSettings() {
  try { return JSON.parse(localStorage.getItem(BRAND_CACHE_KEY) || "null"); } catch { return null; }
}

function NavItems({ onNavigate, role, sectionPortal }) {
  const items = role === "maestra" ? NAV.filter((n) => ["/diario", "/presenze", "/comunicazioni", "/documenti-genitori", "/deleghe", "/buste-paga-maestra"].includes(n.to) && !(sectionPortal && n.to === "/buste-paga-maestra")) : NAV.filter((n) => n.to !== "/buste-paga-maestra");
  return (
    <nav className="flex flex-col gap-1 px-3">
      {items.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          data-testid={n.testid}
          onClick={onNavigate}
          style={({ isActive }) => isActive ? { backgroundColor: "var(--brand-primary)" } : undefined}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
              isActive
                ? "bg-[#E07A5F] text-white shadow-md"
                : "text-[#5b5248] hover:bg-[#eadfce] hover:text-[#3D405B]"
            }`
          }
        >
          <n.icon className="w-4.5 h-4.5 w-5 h-5" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [teacherPushEnabled, setTeacherPushEnabled] = useState(false);
  // Il layout viene ricreato quando cambia pagina: il cache evita che ricompaia
  // per un istante il vecchio riquadro/logo prima della risposta del server.
  const [settings, setSettings] = useState(cachedSettings);

  useEffect(() => { api.get("/impostazioni").then(({ data }) => {
    setSettings(data);
    localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(data));
    document.documentElement.style.setProperty("--brand-primary", data.colore_primario || "#E07A5F");
    document.documentElement.style.setProperty("--brand-secondary", data.colore_secondario || "#81B29A");
    document.documentElement.style.setProperty("--brand-background", data.colore_sfondo || "#FAF6F0");
  }).catch(() => {}); }, []);

  useEffect(() => {
    if (user?.role === "maestra") setTeacherPushEnabled(localStorage.getItem(`teacher_push_notifications_${user.id}`) === "1" && "Notification" in window && Notification.permission === "granted");
  }, [user]);

  useEffect(() => {
    // Il timeout di inattività appartiene soltanto ai portali condivisi delle
    // maestre. L'account amministratore non deve mai ereditarlo, nemmeno se
    // nel browser sono rimasti dati di una precedente sessione di sezione.
    if (user?.role === "admin") {
      sessionStorage.removeItem("section_portal_token");
      sessionStorage.removeItem("section_portal_expires_at");
      sessionStorage.removeItem("section_portal_return");
      return undefined;
    }
    if (!user?.section_portal || user?.role !== "maestra") return undefined;
    const IDLE_MS = 5 * 60 * 1000;
    let expiresAt = Number(sessionStorage.getItem("section_portal_expires_at"));
    if (!expiresAt) {
      expiresAt = Date.now() + IDLE_MS;
      sessionStorage.setItem("section_portal_expires_at", String(expiresAt));
    }
    let ending = false;
    let timeout;
    const expireSession = () => {
      if (ending) return;
      if (Date.now() < expiresAt) {
        scheduleExpiry();
        return;
      }
      ending = true;
      const returnToSection = sessionStorage.getItem("section_portal_return") || localStorage.getItem("section_portal_return");
      sessionStorage.removeItem("section_portal_token");
      sessionStorage.removeItem("section_portal_expires_at");
      setUser(false);
      if (!returnToSection) {
        window.location.replace("/login");
        return;
      }
      window.location.replace(returnToSection);
    };
    function scheduleExpiry() {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(expireSession, Math.max(0, expiresAt - Date.now()));
    }
    const registerActivity = () => {
      if (ending || document.hidden) return;
      expiresAt = Date.now() + IDLE_MS;
      sessionStorage.setItem("section_portal_expires_at", String(expiresAt));
      scheduleExpiry();
    };
    const onVisible = () => { if (!document.hidden) expireSession(); };
    const activityEvents = ["pointerdown", "touchstart", "keydown", "scroll"];
    activityEvents.forEach((eventName) => window.addEventListener(eventName, registerActivity, { passive: true }));
    document.addEventListener("visibilitychange", onVisible);
    scheduleExpiry();
    return () => {
      window.clearTimeout(timeout);
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, registerActivity));
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user?.section_portal, user?.role, setUser, navigate]);

  const activateTeacherNotifications = async () => {
    try {
      await enablePushNotifications(api);
      localStorage.setItem(`teacher_push_notifications_${user.id}`, "1");
      setTeacherPushEnabled(true);
      toast.success("Notifiche attivate");
    } catch (error) { toast.error(error.message); }
  };

  const handleLogout = async () => {
    const sectionSession = user?.role === "maestra" && (
      user?.section_portal ||
      Boolean(sessionStorage.getItem("section_portal_token")) ||
      Boolean(sessionStorage.getItem("section_portal_return"))
    );
    if (sectionSession) {
      const returnToSection = sessionStorage.getItem("section_portal_return") || localStorage.getItem("section_portal_return");
      sessionStorage.removeItem("section_portal_token");
      sessionStorage.removeItem("section_portal_expires_at");
      setUser(false);
      window.location.replace(returnToSection || "/login");
      return;
    }
    await logout();
    navigate("/login");
  };

  const parentLink = `${window.location.origin}/genitori`;

  return (
    <div className="min-h-screen bg-[#FAF6F0]" style={{ backgroundColor: "var(--brand-background)" }} data-testid="app-layout">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-[#F3ECE0] border-r border-[#e8d9c8] z-30">
        <div className="flex items-center gap-3 px-6 pt-7 pb-6">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center shadow-md overflow-hidden bg-white">
            {settings?.logo_path ? <img src={`${API}/branding/logo?v=${encodeURIComponent(settings.logo_path)}`} alt="Logo" className="w-full h-full object-contain bg-white" /> : null}
          </div>
          <div>
            <p className="font-heading text-lg font-semibold text-[#3D405B] leading-tight">{settings?.nome_azienda || "APiccoliPassi"}</p>
            <p className="text-[11px] text-[#a89a8a] font-medium">Nido & Scuola Infanzia</p>
          </div>
        </div>
        <NavItems role={user?.role} sectionPortal={user?.section_portal} />
        <div className="mt-auto p-4 space-y-3">
          {user?.role === "maestra" && !teacherPushEnabled && <button onClick={activateTeacherNotifications} className="flex w-full items-center gap-2 rounded-xl border border-[#e0d2bf] bg-white/70 px-4 py-2.5 text-xs font-semibold text-[#4a7a63] hover:bg-white transition"><Bell className="w-4 h-4"/> Attiva notifiche</button>}
          {user?.role === "admin" && <a
            href={parentLink}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-parent-portal-link"
            className="flex items-center gap-2 rounded-xl border border-[#e0d2bf] bg-white/70 px-4 py-2.5 text-xs font-semibold text-[#b0563a] hover:bg-white transition"
          >
            <Link2 className="w-4 h-4" /> Portale Genitori
          </a>}
          <div className="flex items-center gap-3 rounded-xl bg-white/70 border border-[#e0d2bf] px-4 py-3">
            <div className="w-9 h-9 rounded-full bg-[#81B29A] text-white flex items-center justify-center font-heading font-semibold">
              {(user?.name || "U")[0].toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-[#3D405B] truncate" data-testid="sidebar-user-name">{user?.name}</p>
              <p className="text-[11px] text-[#a89a8a] capitalize">{user?.role}</p>
            </div>
            <button onClick={handleLogout} data-testid="logout-button" title="Esci"
              className="text-[#b0563a] hover:text-[#D06346] transition">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <div className="lg:hidden sticky top-0 z-40 w-full bg-[#F3ECE0]/90 backdrop-blur border-b border-[#e8d9c8]">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-white overflow-hidden">
              {settings?.logo_path ? <img src={`${API}/branding/logo?v=${encodeURIComponent(settings.logo_path)}`} alt="Logo" className="w-full h-full object-contain bg-white rounded-xl" /> : null}
            </div>
            <span className="min-w-0 truncate font-heading font-semibold text-[#3D405B]">{settings?.nome_azienda || "APiccoliPassi"}</span>
          </div>
          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-button" className="p-2 text-[#3D405B]">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {open && (
          <div className="pb-4 max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain">
            {user?.role === "maestra" && !teacherPushEnabled && <button onClick={activateTeacherNotifications} className="mx-3 mb-2 flex w-[calc(100%-1.5rem)] items-center gap-3 rounded-xl bg-[#E8F3EE] px-4 py-2.5 text-sm font-semibold text-[#4a7a63]"><Bell className="w-5 h-5"/> Attiva notifiche</button>}
            <NavItems role={user?.role} sectionPortal={user?.section_portal} onNavigate={() => setOpen(false)} />
            <button onClick={handleLogout} data-testid="logout-button-mobile"
              className="mx-3 mt-2 flex w-[calc(100%-1.5rem)] items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold text-[#b0563a] hover:bg-[#eadfce]">
              <LogOut className="w-5 h-5" /> Esci
            </button>
          </div>
        )}
      </div>

      <main className="w-full lg:w-auto lg:ml-64 min-w-0 px-3 sm:px-8 py-6 sm:py-8 max-w-7xl">{children}</main>
    </div>
  );
}
